/**
 * Thin wrapper around the Agents SDK React client pinned to `TicketAgent`.
 * Keeps the instance-name convention (<orgId>--<ticketId>) in one place.
 *
 * Auth: we pass the Clerk session token as a `query` param so the Worker's
 * `verifyAgentUpgrade` can check it before upgrading the WebSocket.
 *
 * Returns `null` when no ticket is selected so callers can skip using
 * `agent.call(...)` entirely (the SDK has no first-class enabled flag).
 */

import { useAgent } from 'agents/react';
import { useAuth } from '@clerk/react';
import { useEffect, useRef, useState } from 'react';
import type { TicketState } from '../../../src/types';

export function useTicketAgent(
  orgId: string | null | undefined,
  ticketId: string | null | undefined,
  onState?: (s: TicketState) => void,
) {
  const { getToken } = useAuth();
  const [, setTick] = useState(0);
  const tokenRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getToken().then((t) => {
      if (cancelled) return;
      tokenRef.current = t ?? null;
      setTick((x) => x + 1);
    });
    return () => { cancelled = true; };
  }, [getToken]);

  // Use a name even when no ticket is selected — the SDK always connects.
  // We gate usage on the caller side (Inbox only calls agent.call when a
  // ticket is selected, preventing log spam from a perma-open socket).
  const fullName = orgId && ticketId ? `${orgId}--${ticketId}` : `${orgId ?? 'anon'}--idle`;

  return useAgent<TicketState>({
    agent: 'TicketAgent',
    name: fullName,
    query: async () => ({ token: tokenRef.current ?? '' }),
    queryDeps: [tokenRef.current],
    onStateUpdate: onState,
  });
}
